import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as CONSTANT from "../../constants";
import { ArrowRightIcon } from "../../images/svgIcons/svg";
import useCheckMobileScreen from "../../hooks/useCheckMobileScreen";
import useGetMenuCategories from "../../react-query-hooks/useGetCategories";
import MenuDropdownBrazeBanner from "./MenuDropdownBrazeBanner";
import { getCachedBannerHeight } from "../../helpers/brazeBannerHeightCache";
import { MENU_DROPDOWN_BANNER_WEB } from "../../helpers/brazePlacementIds";
import {
  PULSE_CLICK_ACTION,
  trackMenuPageClick,
} from "../../analytics/pulseClickIds";
import styles from "./MenuCategoryDropdown.module.scss";

interface MenuCategoryDropdownProps {
  label: string;
  className?: string;
}

const getMenuImageUrl = (name: string) =>
  `${CONSTANT.REACT_APP_CLOUD_FRONT}/images/Menu/${name
    .replace(/'/g, "")
    .toLowerCase()}.png`;

const DROPDOWN_ITEM_HEIGHT = 70;
const MAX_VISIBLE_ITEMS = 7;
const VIEWPORT_BOTTOM_PADDING = 16;
const DEFAULT_BANNER_HEIGHT = 128;
const PANEL_TOP_OFFSET_RATIO = 1.05;

const getBannerHeightForLayout = (measuredHeight?: number) => {
  if (measuredHeight && measuredHeight > 0) return measuredHeight;
  return getCachedBannerHeight(MENU_DROPDOWN_BANNER_WEB) ?? DEFAULT_BANNER_HEIGHT;
};

const getPanelTopFromWrapper = (wrapper: HTMLElement) => {
  const { top, height } = wrapper.getBoundingClientRect();
  return top + height * PANEL_TOP_OFFSET_RATIO;
};

const getMenuMaxHeight = (panelTop: number, bannerHeight: number) => {
  const availableHeight =
    window.innerHeight - panelTop - bannerHeight - VIEWPORT_BOTTOM_PADDING;
  const maxItemsHeight = DROPDOWN_ITEM_HEIGHT * MAX_VISIBLE_ITEMS;
  const cappedHeight = Math.min(maxItemsHeight, availableHeight);
  const visibleItems = Math.max(
    1,
    Math.min(MAX_VISIBLE_ITEMS, Math.floor(cappedHeight / DROPDOWN_ITEM_HEIGHT)),
  );

  return visibleItems * DROPDOWN_ITEM_HEIGHT;
};

const MenuCategoryDropdown: React.FC<MenuCategoryDropdownProps> = ({
  label,
  className = "",
}) => {
  const isMobile = useCheckMobileScreen();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: subMenus = [] } = useGetMenuCategories();

  const updateMenuMaxHeight = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const panelTop = getPanelTopFromWrapper(wrapper);
    const bannerHeight = getBannerHeightForLayout(bannerRef.current?.offsetHeight);
    const nextMaxHeight = getMenuMaxHeight(panelTop, bannerHeight);
    wrapper.style.setProperty("--dropdown-menu-max-height", `${nextMaxHeight}px`);
  }, []);

  useLayoutEffect(() => {
    if (isMobile) return;

    updateMenuMaxHeight();

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const resizeObserver = new ResizeObserver(updateMenuMaxHeight);
    resizeObserver.observe(wrapper);
    if (bannerRef.current) {
      resizeObserver.observe(bannerRef.current);
    }

    window.addEventListener("resize", updateMenuMaxHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateMenuMaxHeight);
    };
  }, [isMobile, updateMenuMaxHeight, subMenus.length]);

  useEffect(() => {
    if (!isOpen && menuListRef.current) {
      menuListRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMobile) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isMobile]);

  if (isMobile) {
    return null;
  }

  const handleCategoryClick = (categoryName: string) => {
    trackMenuPageClick({
      action: PULSE_CLICK_ACTION.TAP_MENU_CATEGORY,
      label: categoryName,
      page_path: location.pathname,
    });
    navigate(
      `${CONSTANT.ROUTE_MENU}/${categoryName.replace(/\s/g, "%20")}`
    );
    setIsOpen(false);
  };

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles.dropdownWrapper} ${isOpen ? styles.open : ""}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className={`${styles.menuTrigger} ${className}`}
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {label}
        <span className={styles.menuArrow} aria-hidden="true" />
      </button>
      <div className={styles.dropdownPanel} aria-hidden={!isOpen}>
        <ul ref={menuListRef} className={styles.dropdownMenu}>
          {subMenus.map((subMenu) => (
            <li key={subMenu.id ?? subMenu.name} className={styles.dropdownItem}>
              <div
                className={styles.dropdownLink}
                role="button"
                tabIndex={isOpen ? 0 : -1}
                onClick={() => handleCategoryClick(subMenu.name)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleCategoryClick(subMenu.name);
                  }
                }}
              >
                <div className={styles.dropdownImg}>
                  <img
                    src={getMenuImageUrl(subMenu.name)}
                    alt={subMenu.name}
                  />
                </div>
                <span className={styles.dropdownText}>{subMenu.name}</span>
                <div className={styles.dropdownArrow}>
                  <ArrowRightIcon />
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div ref={bannerRef} className={styles.dropdownBanner}>
          <MenuDropdownBrazeBanner />
        </div>
      </div>
    </div>
  );
};

export default MenuCategoryDropdown;
